import React, { useEffect, useRef, useCallback, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useDispatch, useSelector } from "react-redux";
import { fetchReports } from "../../../../store/Reports/reportsThunks";
import { getReportColumns } from "./ReportDataGridColumns";

const ReportDataGrid = ({ apiRef }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const { reports, loading, currentPage, hasMore, filters, sortModel } =
    useSelector((state) => state.reports);

  const [filterModel, setFilterModel] = useState({ items: [] });
  const [localSortModel, setLocalSortModel] = useState([]);

  // Load initial page
  useEffect(() => {
    dispatch(fetchReports({ page: 1 }));
  }, [dispatch]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return;
    const container = containerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      dispatch(fetchReports({ page: currentPage + 1 }));
    }
  }, [loading, hasMore, currentPage, dispatch]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Server-side filtering
  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    const validFilters = newFilterModel.items.filter(
      (f) => f.value !== undefined && f.value !== ""
    );

    dispatch(
      fetchReports({
        page: 1,
        filters: validFilters,
        sortModel: localSortModel,
      })
    );
  };

  // Server-side sorting
  const handleSortModelChange = (newSortModel) => {
    setLocalSortModel(newSortModel);

    dispatch(
      fetchReports({
        page: 1,
        filters: filterModel.items.filter(
          (f) => f.value !== undefined && f.value !== ""
        ),
        sortModel: newSortModel,
      })
    );
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }} ref={containerRef}>
      <DataGridPro
        apiRef={apiRef}
        rows={reports}
        columns={getReportColumns()}
        loading={loading}
        getRowId={(row) => row.id}
        sortingMode="server"
        filterMode="server"
        onFilterModelChange={handleFilterModelChange}
        onSortModelChange={handleSortModelChange}
        filterDebounceMs={500}
        hideFooter
        disableExtendRowFullWidth
        getDetailPanelHeight={() => "auto"}
        hideDetailPanelToggle={true}
        initialState={{
          columns: {
            columnVisibilityModel: {
              function: false,
              route: false,
              area: false,
            },
          },
        }}
        slotProps={{
          filterPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              "& .MuiButton-root": {
                backgroundColor: theme.palette.background.button,
                color: theme.palette.common.white,
                textTransform: "none",
                px: 2,
                "&:hover": {
                  backgroundColor: theme.palette.background.button,
                },
              },
              "& .MuiIconButton-root": {
                color: theme.palette.text.primary,
              },
            },
          },
          columnsPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          },
        }}
      />
    </Box>
  );
};

export default ReportDataGrid;
